import { Card, CardContent } from "@/components/ui/card";
import { Clock, Sun, Moon } from "lucide-react";
import { EnhancedEnergyChart } from "@/components/energy/EnhancedEnergyChart";
import { EnergyBadge } from "@/components/meeting/EnergyBadge";
import { getEnergyForecastSync, getEnergyBlock } from "@/lib/energy";
import { energyTasks, Task } from "@/lib/tasks";
import { useMemo } from "react";

function  getRandomTasks(tasks: Task[] | undefined, count: number): Task[] {
  if (!tasks || tasks.length === 0) {
    return [];
  }
  
  if (tasks.length <= count) return tasks;
  const shuffled = [...tasks].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
}

export function EnergyDashboardPreview() {
  // Generate a realistic energy forecast for the demo - USE SYNC VERSION
  const energyForecast = useMemo(() => {
    return getEnergyForecastSync({
      wakeTimeHour: 7,
      sleepDuration: 8
    }, undefined, 0);
  }, []);

  // Get current energy level and block type
  const currentHour = new Date().getHours();
  const currentEnergy = energyForecast[currentHour] || 85;
  const currentBlock = getEnergyBlock(currentEnergy);
  
  // Get ideal tasks for current energy zone
  const idealTasks = useMemo(() => {
    if (!energyTasks || !currentBlock || !energyTasks[currentBlock.type]) {
      return [];
    }
    // Find hours in the day that match the current block type and are between 7am and 6pm
    const blockHours: number[] = energyForecast
      .map((energy, hour) => ({ hour, type: getEnergyBlock(energy).type }))
      .filter(({ hour, type }) => type === currentBlock.type && hour >= 7 && hour <= 18)
      .map(({ hour }) => hour);
    // Assign the first N available block hours to the tasks
    return getRandomTasks(energyTasks[currentBlock.type], 3).map((task, i) => ({
      ...task,
      hour: blockHours[i] !== undefined ? blockHours[i] : 7 + i, // fallback to 7, 8, 9
    }));
  }, [currentBlock?.type, energyForecast]);

  return <div className="space-y-1">
      {/* Energy Forecast Card */}
      <Card className="shadow-2xl bg-white border-none rounded-3xl mt-10 lg:-mt-10 relative z-0">
        <CardContent className="lg:p-10">
          <div className="flex items-center gap-3 mb-4">
            <div>
              <h3 className="font-semibold text-xl mb-2">Personalized Energy Curve</h3>
              <EnergyBadge energyPreference={currentBlock.type} />
            </div>
          </div>
          <p className="font-medium text-zinc-800 mt-5">
            {currentBlock.description}
          </p>


          
          {/* Real Interactive Energy Chart */}
          <div className="h-32 my-2 rounded-lg border-0 bg-gradient-to-r from-green-50/30 via-blue-50/30 to-purple-50/30">
            <EnhancedEnergyChart 
              energyForecast={energyForecast} 
              height={128} 
              showCurrentTime={true} 
              enableTooltip={true} 
              simplified={true} 
              wakeTimeHour={7} 
              startHour={7}
              endHour={22}
            />
          </div>
          
          <div className="flex items-center justify-between text-sm text-muted-foreground mb-2 -mt-1 lg:-mt-2">
            <div className="flex items-center gap-1">
              <Sun className="w-4 h-4 text-yellow-500" />
              <span>7am</span>
            </div>
            <div className="flex items-center gap-1">
              <Moon className="w-4 h-4 text-blue-400" />
              <span>10pm</span>
            </div>
          </div>

          <p className="font-medium text-zinc-800 mt-5">
            Your schedule now synced with your biology
          </p>

          
          {/* Calendar-style Ideal Tasks Timeline */}
          {idealTasks.length > 0 && (
            <div className="mt-1 hidden sm:block">
              <div className="border border-zinc-200/50 rounded-md bg-muted/40 overflow-hidden">
                {/* Timeline: 3 compact timeslots, only block hours for current type */}
                {(() => {
                  // Get up to 3 unique hours for the current block type, only between 7am and 6pm
                  let blockHours = energyForecast
                    .map((energy, hour) => ({ hour, type: getEnergyBlock(energy).type }))
                    .filter(({ hour, type }) => type === currentBlock.type && hour >= 7 && hour <= 18)
                    .map(({ hour }) => hour)
                    .slice(0, 3);
                  // If less than 3, fill with next hours in the 7–18 range
                  let fallbackHour = 7;
                  while (blockHours.length < 3 && fallbackHour <= 18) {
                    if (!blockHours.includes(fallbackHour)) blockHours.push(fallbackHour);
                    fallbackHour++;
                  }
                  // Sort hours in ascending order (AM to PM)
                  blockHours = blockHours.sort((a, b) => a - b);
                  return blockHours.map((hour) => {
                    const task = idealTasks.find(t => t.hour === hour);
                    return (
                      <div
                        key={hour}
                        className="flex border border-zinc-50 min-h-[36px] bg-white/70 hover:bg-muted/60 transition-colors"
                      >
                        <div className="w-16 p-2 flex items-center justify-end border-r bg-transparent text-xs font-bold text-zinc-700 select-none">
                          {hour === 12 ? '12 PM' : hour > 12 ? `${hour - 12} pm` : `${hour} am`}
                        </div>
                        <div className="flex-1 flex items-center px-2 min-w-0">
                          {task && (
                            <div className="flex items-center gap-1 px-1.5 py-0.5 bg-green-100 border-l-4 border-green-500 rounded-sm shadow-sm animate-fade-in overflow-x-auto flex-nowrap w-full">
                              {task.icon && (
                                <task.icon className="w-3 h-3 text-green-700 shrink-0" />
                              )}
                              <span className="text-sm font-semibold text-green-900 truncate max-w-full">
                                {task.name}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Meeting Links Grid */}
      
    </div>;
}
